/**
 * tingl custom auth
 * Funny username + password → Cloud Function → Firebase custom token.
 * The custom token gives us request.auth in Firestore rules.
 */

import { db, auth, functions } from './firebase'
import { doc, getDoc, setDoc } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { signInWithCustomToken, signOut as fbSignOut } from 'firebase/auth'

export interface TinglUser {
  uid: string
  username: string
  displayName: string
  currentVibe?: string | null
  fcmToken?: string | null
}

const SESSION_KEY = 'tingl_session'

export function getSession(): { uid: string; username: string } | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}

function setSession(uid: string, username: string) {
  localStorage.setItem(SESSION_KEY, JSON.stringify({ uid, username }))
}

export function clearSession() {
  localStorage.removeItem(SESSION_KEY)
}

// ─── Sign up — username is auto-generated client-side, passed in ────────────
export async function signUp(
  username: string,
  password: string,
  recovery?: { email?: string; phone?: string }
): Promise<TinglUser> {
  const fn = httpsCallable(functions, 'createUser')
  const res = await fn({
    username,
    password,
    recoveryEmail: recovery?.email || null,
    recoveryPhone: recovery?.phone || null,
  })
  const { uid, token } = res.data as { uid: string; username: string; token: string }
  await signInWithCustomToken(auth, token)
  setSession(uid, username)
  return { uid, username, displayName: username }
}

// ─── Sign in ─────────────────────────────────────────────────────────────────
export async function signIn(username: string, password: string): Promise<TinglUser> {
  const fn = httpsCallable(functions, 'loginUser')
  const res = await fn({ username, password })
  const { uid, username: realName, token } =
    res.data as { uid: string; username: string; token: string }
  await signInWithCustomToken(auth, token)
  setSession(uid, realName)
  return { uid, username: realName, displayName: realName }
}

// ─── Sign out ────────────────────────────────────────────────────────────────
export async function signOut() {
  clearSession()
  await fbSignOut(auth).catch(() => {})
  window.location.href = '/'
}

// ─── Current user profile ────────────────────────────────────────────────────
export async function getCurrentUser(): Promise<TinglUser | null> {
  const session = getSession()
  if (!session) return null
  const snap = await getDoc(doc(db, 'users', session.uid))
  if (!snap.exists()) return null
  const d = snap.data()
  return {
    uid: session.uid,
    username: d.username,
    displayName: d.displayName || d.username,
    currentVibe: d.currentVibe || null,
  }
}

// ─── Save FCM token ──────────────────────────────────────────────────────────
export async function saveFcmToken(token: string) {
  const session = getSession()
  if (!session) return
  await setDoc(doc(db, 'users', session.uid), { fcmToken: token }, { merge: true })
}

// ─── Update my current vibe status ───────────────────────────────────────────
export async function setMyVibe(emoji: string, label: string) {
  const session = getSession()
  if (!session) return
  await setDoc(
    doc(db, 'users', session.uid),
    { currentVibe: emoji, currentVibeLabel: label },
    { merge: true }
  )
}
